import {inject, Injectable} from '@angular/core';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {CreateInvoiceRequest, InvoiceService, RunDailyInvoicingResponse, UpdateInvoiceRequest} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';
import {ToastService} from '../../../services/toast.service';

@Injectable({providedIn: 'root'})
export class InvoiceStore {
  private readonly invoiceService = inject(InvoiceService);
  private readonly queryClient = inject(QueryClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  createMutation = injectMutation(() => ({
    mutationFn: (request: CreateInvoiceRequest) => lastValueFrom(this.invoiceService.createInvoice(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICING_PREVIEW]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMER_BILLINGS]});
    },
  }));

  updateMutation = injectMutation(() => ({
    mutationFn: ({id, request}: {id: number; request: UpdateInvoiceRequest}) =>
      lastValueFrom(this.invoiceService.updateInvoice(id, request)),
    onSuccess: (_, {id}) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
      this.queryClient.invalidateQueries({queryKey: ['invoice', id]});
      this.queryClient.invalidateQueries({queryKey: ['invoicePdf']});
      this.queryClient.invalidateQueries({queryKey: ['invoiceAccountingOperations']});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICING_PREVIEW]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMER_BILLINGS]});
    },
  }));

  runDailyInvoicingMutation = injectMutation(() => ({
    mutationFn: () => lastValueFrom(this.invoiceService.runDailyInvoicing()),
    onSuccess: (response: RunDailyInvoicingResponse) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
      this.queryClient.invalidateQueries({queryKey: ['invoice']});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICING_PREVIEW]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMER_BILLINGS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMERS]});
      this.notifyInvoicingFailures(response);
    },
  }));

  sendMutation = injectMutation(() => ({
    mutationFn: ({id, params}: {id: number; params: SendInvoiceParams}) =>
      lastValueFrom(this.invoiceService.sendInvoice(
        id, params.to, params.cc ?? [], params.bcc ?? [], params.object, params.content, params.isProForma,
        params.attachments,
      )),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
    },
  }));

  deleteMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.invoiceService.deleteInvoice(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
      // Deleting a draft releases its billings, which reappear in the invoicing preview.
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICING_PREVIEW]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMER_BILLINGS]});
    },
  }));

  // Le run de facturation ne bloque pas sur un client en échec : il renvoie la liste des anomalies,
  // agrégées ici dans un seul toast (nom + id + message métier traduit par client).
  private notifyInvoicingFailures(response: RunDailyInvoicingResponse) {
    if (!response.failures.length) {
      return;
    }
    const detail = response.failures
      .map(failure => `${failure.customerName} (#${failure.customerId}) : ${this.translateFailure(failure.errorCode)}`)
      .join(' • ');
    this.toastService.showMessage({
      severity: 'error',
      summary: 'invoicing-log.title',
      detail,
    });
  }

  private translateFailure(errorCode: string | null | undefined): string {
    const key = errorCode ? `business-rule.${errorCode}` : 'business-rule.UNKNOWN';
    const translated = this.translateService.instant(key);
    // instant() renvoie la clé telle quelle quand la traduction n'existe pas
    return translated === key ? this.translateService.instant('generic-error.detail') : translated;
  }
}

export interface SendInvoiceParams {
  to: string[];
  object: string;
  content: string;
  isProForma: boolean;
  cc?: string[] | null;
  bcc?: string[] | null;
  attachments: File[];
}
